import { Injectable } from '@angular/core';
import { ChildTableColumn, ChildTableRow } from '../models/child-table.models';
import { RowActionId, RowActionItem } from '../models/child-table-row-actions.models';
import { ChildTableStateService } from './child-table-state.service';
import { ChildTableA11yService } from './child-table-a11y.service';
import { ChildTablePositioningService } from './child-table-positioning.service';

/**
 * Returned by onRowAction for actions that must be dispatched
 * back to the component (EventEmitter, DOM focus, etc.).
 */
export interface RowActionResult {
  actionId: RowActionId;
  rowId: string;
  rowIndex: number;
}

/**
 * Manages row context menu, column filter menu, and row-level clipboard
 * operations (cut / copy / paste). Component-scoped.
 *
 * DOM-dependent references (ViewChild menu components) are passed as
 * parameters rather than injected, keeping the service DOM-free.
 */
@Injectable()
export class ChildTableMenuService<T = any> {
  // --- Row context menu state ---
  openRowMenuId: string | null = null;
  openRowMenuIndex = -1;
  rowMenuItems: RowActionItem[] = [];
  private rowMenuTriggerEl: HTMLElement | null = null;

  // --- Column filter menu state ---
  openColumnMenu: string | null = null;

  // --- Clipboard buffer (row-level cut/copy/paste) ---
  clipboardBuffer: Record<string, any> | null = null;

  constructor(
    private readonly stateService: ChildTableStateService<T>,
    private readonly a11yService: ChildTableA11yService,
    private readonly positioningService: ChildTablePositioningService,
  ) {}

  // ============================================================
  // Row context menu
  // ============================================================

  /**
   * Toggle the row menu from a mouse click on the kebab button.
   * Returns true if the menu was opened (so the component can run detectChanges + position).
   */
  toggleRowMenu(
    rowId: string,
    rowIndex: number,
    event: MouseEvent,
    pasteErrorLocked: boolean,
    displayRowCount: number,
  ): boolean {
    if (pasteErrorLocked) return false;
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement;
    return this.openRowMenuWithTrigger(rowId, rowIndex, trigger, displayRowCount);
  }

  /**
   * Open the row menu for a given trigger element.
   * Returns true if the menu was freshly opened, false if it was toggled closed.
   */
  openRowMenuWithTrigger(
    rowId: string,
    rowIndex: number,
    trigger: HTMLElement,
    displayRowCount: number,
  ): boolean {
    if (this.openRowMenuId === rowId) {
      this.closeRowMenu();
      return false;
    }

    this.closeColumnMenuState();
    this.openRowMenuId = rowId;
    this.openRowMenuIndex = rowIndex;
    this.rowMenuItems = this.buildRowMenuItems(rowIndex, displayRowCount);
    this.rowMenuTriggerEl = trigger;
    return true;
  }

  /**
   * Position the row menu after the component has run detectChanges
   * so the menu element exists in the DOM.
   */
  positionRowMenu(menuEl: HTMLElement): void {
    if (!menuEl || !this.rowMenuTriggerEl) return;
    this.positioningService.openMenu('row', this.rowMenuTriggerEl, menuEl, () => {
      this.closeRowMenu();
    });
  }

  closeRowMenu(): void {
    this.positioningService.closeMenu('row');
    this.openRowMenuId = null;
    this.openRowMenuIndex = -1;
    this.rowMenuItems = [];

    if (this.rowMenuTriggerEl) {
      const el = this.rowMenuTriggerEl;
      this.rowMenuTriggerEl = null;
      setTimeout(() => el.focus(), 0);
    }
  }

  /**
   * Handle a row menu action.
   *
   * Actions fully handled here (cut, copy, paste, clear) return null.
   * Actions that need component-level orchestration return a RowActionResult.
   */
  onRowAction(
    actionId: RowActionId,
    displayRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
  ): RowActionResult | null {
    if (!this.openRowMenuId) return null;

    const rowId = this.openRowMenuId;
    const rowIndex = this.openRowMenuIndex;

    switch (actionId) {
      case 'cut':
        this.cutRow(rowId, displayRows, visibleColumns);
        return null;
      case 'copy':
        this.copyRow(rowId, displayRows, visibleColumns);
        return null;
      case 'paste':
        this.pasteIntoRow(rowId, visibleColumns);
        return null;
      case 'clear':
        this.clearRowAction();
        return null;
      default:
        return { actionId, rowId, rowIndex };
    }
  }

  // --- Clipboard operations ---

  cutRow(
    rowId: string,
    displayRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
  ): void {
    const row = displayRows.find((r) => r.id === rowId);
    if (!row) return;
    this.clipboardBuffer = { ...(row.data as any) };
    const tsv = visibleColumns.map((c) => String((row.data as any)[c.key] ?? '')).join('\t');
    navigator.clipboard.writeText(tsv).catch(() => {});
    this.stateService.clearRow(rowId);
    this.a11yService.announce('Row cut');
    this.closeRowMenu();
  }

  copyRow(
    rowId: string,
    displayRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
  ): void {
    const row = displayRows.find((r) => r.id === rowId);
    if (!row) return;
    this.clipboardBuffer = { ...(row.data as any) };
    const tsv = visibleColumns.map((c) => String((row.data as any)[c.key] ?? '')).join('\t');
    navigator.clipboard.writeText(tsv).catch(() => {});
    this.a11yService.announce('Row copied');
    this.closeRowMenu();
  }

  pasteIntoRow(
    rowId: string,
    visibleColumns: ChildTableColumn<T>[],
  ): void {
    if (!this.clipboardBuffer) return;
    for (const col of visibleColumns) {
      if (!col.editable) continue;
      const val = this.clipboardBuffer[col.key];
      if (val !== undefined) {
        this.stateService.updateCell(rowId, col.key, val);
      }
    }
    this.a11yService.announce('Pasted');
    this.closeRowMenu();
  }

  private clearRowAction(): void {
    if (!this.openRowMenuId) return;
    this.stateService.clearRow(this.openRowMenuId);
    this.a11yService.announce('Row cleared');
    this.closeRowMenu();
  }

  private buildRowMenuItems(rowIndex: number, displayRowCount: number): RowActionItem[] {
    return [
      { kind: 'title', label: `Row ${rowIndex + 1}` },
      { kind: 'action', id: 'cut', label: 'Cut' },
      { kind: 'action', id: 'copy', label: 'Copy' },
      { kind: 'action', id: 'paste', label: 'Paste', disabled: !this.clipboardBuffer },
      // { kind: 'divider' },
      // { kind: 'action', id: 'moveUp', label: 'Move Up 1 Row', disabled: rowIndex <= 0 },
      // { kind: 'action', id: 'moveDown', label: 'Move Down 1 Row', disabled: rowIndex >= displayRowCount - 1 },
      // { kind: 'action', id: 'customMove', label: 'Custom Move' },
      { kind: 'divider' },
      { kind: 'action', id: 'insertAbove', label: 'Insert 1 Row Above' },
      { kind: 'action', id: 'insertBelow', label: 'Insert 1 Row Below' },
      { kind: 'divider' },
      { kind: 'action', id: 'clear', label: 'Clear Row' },
      { kind: 'action', id: 'delete', label: 'Delete Row', danger: true },
    ];
  }

  // ============================================================
  // Column filter menu
  // ============================================================

  /**
   * Toggle the column filter menu open/closed.
   *
   * Manages own state (openColumnMenu) and Popper positioning.
   * The component must supply the menu DOM element (ViewChild) and
   * callbacks for focus management since those live in the component.
   *
   * Returns the column index for the toggled column (-1 if not found).
   */
  toggleColumnMenu(
    columnKey: string,
    event: Event,
    pasteErrorLocked: boolean,
    visibleColumns: ChildTableColumn<T>[],
    callbacks: {
      onCloseAndRefocus: () => void;
      onFocusFirstItem: () => void;
      getMenuElement: () => HTMLElement | null | undefined;
      detectChanges: () => void;
    },
  ): number {
    if (pasteErrorLocked) return -1;
    event.stopPropagation();
    this.closeRowMenu();

    const colIdx = visibleColumns.findIndex(c => c.key === columnKey);
    const button = event.target as HTMLElement;

    if (this.openColumnMenu === columnKey) {
      this.closeColumnMenuState();
      callbacks.onCloseAndRefocus();
      return colIdx;
    }

    this.closeColumnMenuState();
    this.openColumnMenu = columnKey;
    callbacks.detectChanges();

    const menuEl = callbacks.getMenuElement();
    if (menuEl) {
      this.positioningService.openMenu('column', button, menuEl, () => {
        this.openColumnMenu = null;
        callbacks.detectChanges();
        callbacks.onCloseAndRefocus();
      });
      setTimeout(() => callbacks.onFocusFirstItem());
    }

    return colIdx;
  }

  /**
   * Close the column menu state (Popper + flag). Does not touch row menu.
   */
  closeColumnMenuState(): void {
    this.positioningService.closeMenu('column');
    this.openColumnMenu = null;
  }

  getUniqueColumnValues(columnKey: string, displayRows: ChildTableRow<T>[]): string[] {
    const values = new Set<string>();
    for (const row of displayRows) {
      const value = (row.data as any)[columnKey];
      if (value !== null && value !== undefined) values.add(String(value));
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }

  onColumnFilterApply(
    columnKey: string,
    filter: { operator: string; value: string },
  ): void {
    this.stateService.removeFilter(columnKey);
    if (filter.operator && filter.operator !== '') {
      this.stateService.addFilter({ column: columnKey, operator: filter.operator as any, value: filter.value });
    }
    this.closeColumnMenuState();
    this.a11yService.announce('Filter applied');
  }

  cancelColumnMenu(columnKey: string): void {
    this.stateService.removeFilter(columnKey);
    this.closeColumnMenuState();
  }
}
